import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { KeyRound, Mail, ShieldCheck, Sparkles, User, Wallet, LogOut, Package, RefreshCw, Key, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container, meta } from "@/components/store/page-parts";
import { useStore } from "@/lib/store-context";
import { toast } from "sonner";

export const Route = createFileRoute("/account")({
  head: () => meta("Your Account — betteraccess.store", "Sign in to access your digital orders, delivery details and support history."),
  component: Account,
});

const perks: [typeof ShieldCheck, string, string][] = [
  [ShieldCheck, "Protected order history", "Every purchase stored securely in one place"],
  [KeyRound, "Private access details", "Credentials visible only to you in your personal vault"],
  [Mail, "Delivery notifications", "Automated email alerts the moment an order is fulfilled"],
];

function Account() {
  const { user, login, logout, refreshUser } = useStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"signin" | "create" | "forgot">("signin");
  const [loading, setLoading] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Forgot password OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Handle Login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "Login failed");
      }
      login(data.user, data.token);
      toast.success(`Welcome back, ${data.user.name}!`);
    } catch (err: any) {
      toast.error(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Handle Register
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error("All fields are required");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "Registration failed");
      }
      login(data.user, data.token);
      toast.success("Account created successfully!");
    } catch (err: any) {
      toast.error(err.message || "Could not register account");
    } finally {
      setLoading(false);
    }
  };

  // Handle Send OTP for Password Reset
  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "Failed to send reset code");
      }
      setOtpSent(true);
      toast.success("A 6-digit OTP code has been sent to your email.");
    } catch (err: any) {
      toast.error(err.message || "Could not request password reset");
    } finally {
      setLoading(false);
    }
  };

  // Handle Reset Password with OTP
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || !newPassword || newPassword.length < 6) {
      toast.error("Enter valid OTP code and new password (min 6 characters)");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otpCode.trim(), newPassword }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "Reset failed");
      }
      toast.success("Password reset successful! You can now log in.");
      setTab("signin");
      setOtpSent(false);
    } catch (err: any) {
      toast.error(err.message || "Invalid or expired OTP");
    } finally {
      setLoading(false);
    }
  };

  // If user is already logged in, show account dashboard
  if (user) {
    return (
      <Container className="py-8 sm:py-12">
        <div className="flex flex-col justify-between gap-4 border-b pb-6 sm:flex-row sm:items-center">
          <div>
            <span className="rounded bg-primary/10 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-primary">
              Member Profile
            </span>
            <h1 className="mt-2 text-3xl font-black">{user.name}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
          <div className="flex gap-2">
            {user.role === "admin" && (
              <Button asChild className="gap-1.5 font-bold">
                <Link to="/admin">Admin Panel <ArrowRight className="size-4" /></Link>
              </Button>
            )}
            <Button variant="outline" onClick={logout} className="gap-1.5">
              <LogOut className="size-4" /> Sign out
            </Button>
          </div>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="premium-panel p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-muted-foreground">WALLET BALANCE</span>
              <Wallet className="size-5 text-emerald-500" />
            </div>
            <p className="mt-3 text-3xl font-black text-foreground">৳{user.wallet_balance.toFixed(2)}</p>
            <p className="mt-1 text-xs text-muted-foreground">Usable for instant 1-click checkout</p>
          </div>

          <div className="premium-panel p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-muted-foreground">ORDERS &amp; LICENSES</span>
              <Package className="size-5 text-primary" />
            </div>
            <p className="mt-3 text-lg font-bold">Manage Orders</p>
            <Button asChild size="sm" variant="outline" className="mt-3">
              <Link to="/account/orders">View All Orders →</Link>
            </Button>
          </div>

          <div className="premium-panel p-5 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-muted-foreground">ACCOUNT ROLE</span>
              <ShieldCheck className="size-5 text-blue-500" />
            </div>
            <p className="mt-3 text-lg font-bold capitalize">{user.role || "Customer"}</p>
            <p className="mt-1 text-xs text-muted-foreground">Registered on BetterAccess Store</p>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-black">Quick Actions</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button asChild variant="outline">
              <Link to="/products">Shop Catalog</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/support">Contact WhatsApp Support</Link>
            </Button>
            {user.role === "admin" && (
              <Button asChild>
                <Link to="/admin">Go to Control Center</Link>
              </Button>
            )}
          </div>
        </div>
      </Container>
    );
  }

  // Not logged in: Show Sign In / Sign Up / Forgot Password form
  return (
    <Container className="py-6 sm:py-14">
      <div className="mx-auto grid max-w-5xl overflow-hidden rounded-2xl border bg-card shadow-card lg:grid-cols-[1.05fr_1fr]">
        <div className="relative hidden overflow-hidden bg-ink p-10 text-ink-foreground lg:block">
          <div className="absolute -right-16 -top-16 size-56 rounded-full bg-primary/25 blur-3xl" />
          <p className="text-xs font-extrabold uppercase tracking-widest text-primary">Your access vault</p>
          <h2 className="mt-4 text-3xl font-black leading-tight">Everything you buy, safe in one place.</h2>
          <p className="mt-4 max-w-sm text-sm leading-6 text-ink-muted">
            Track delivery, review activation steps and reach support without digging through old messages.
          </p>
          <div className="mt-9 space-y-5">
            {perks.map(([Icon, title, copy]) => (
              <div key={title} className="flex items-start gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/15 text-primary">
                  <Icon className="size-4" />
                </span>
                <span>
                  <b className="block text-sm">{title}</b>
                  <small className="text-ink-muted">{copy}</small>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 sm:p-9">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-primary">
            <Sparkles className="size-3" />
            BetterAccess account
          </span>
          <h1 className="mt-3 text-2xl font-black sm:text-3xl">
            {tab === "signin" ? "Welcome back" : tab === "create" ? "Create your account" : "Reset Password"}
          </h1>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {tab === "signin"
              ? "Sign in to view orders and access details."
              : tab === "create"
              ? "One account for every order and renewal."
              : "Enter your email to receive a 6-digit verification code."}
          </p>

          <div className="mt-5 grid grid-cols-2 rounded-lg bg-secondary p-1">
            {(["signin", "create"] as const).map(value => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={`rounded-md px-3 py-2 text-sm font-bold transition ${
                  tab === value ? "bg-background shadow-sm" : "text-muted-foreground"
                }`}
              >
                {value === "signin" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          {tab === "signin" && (
            <form className="mt-6 space-y-4" onSubmit={handleLogin}>
              <Field label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <Field label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setTab("forgot")}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <Button type="submit" size="lg" className="w-full font-bold" disabled={loading}>
                {loading ? <RefreshCw className="size-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          )}

          {tab === "create" && (
            <form className="mt-6 space-y-4" onSubmit={handleRegister}>
              <Field label="Full name" type="text" value={name} onChange={e => setName(e.target.value)} required />
              <Field label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <Field label="Password (min 6 chars)" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              <Button type="submit" size="lg" className="w-full font-bold" disabled={loading}>
                {loading ? <RefreshCw className="size-4 animate-spin" /> : "Create Account"}
              </Button>
            </form>
          )}

          {tab === "forgot" && !otpSent && (
            <form className="mt-6 space-y-4" onSubmit={handleSendOtp}>
              <Field label="Registered Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <Button type="submit" size="lg" className="w-full font-bold" disabled={loading}>
                {loading ? <RefreshCw className="size-4 animate-spin" /> : "Send Reset Code"}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setTab("signin")}
                  className="text-xs font-semibold text-muted-foreground hover:underline"
                >
                  Back to Sign in
                </button>
              </div>
            </form>
          )}

          {tab === "forgot" && otpSent && (
            <form className="mt-6 space-y-4" onSubmit={handleResetPassword}>
              <Field label="6-Digit OTP Code" type="text" value={otpCode} onChange={e => setOtpCode(e.target.value)} required />
              <Field label="New Password (min 6 chars)" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
              <Button type="submit" size="lg" className="w-full font-bold" disabled={loading}>
                {loading ? <RefreshCw className="size-4 animate-spin" /> : "Set New Password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </Container>
  );
}

function Field({
  label,
  type = "text",
  value,
  onChange,
  required = true,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
}) {
  return (
    <label className="block text-xs font-bold">
      {label}
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-2 h-11 w-full rounded-md border bg-background px-3 text-sm font-normal outline-none focus:ring-2 focus:ring-ring"
      />
    </label>
  );
}
